import React from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import styled from 'styled-components';

import { Box, Text, Theme } from 'baukasten';

import { CurrentAccount } from 'types/CurrentAccount';
import { assertUnreachable } from 'types/utilityTypes';
import { useTranslationOverrides } from 'utils/hooks/useTranslationOverrides';

import messages from '../messages';
import { LicenseType } from '../types';

const FeaturesGridLayout = styled(Box)`
  grid-column: 1 / -1;
  grid-row: 2;
  grid-template-columns: repeat(2, minmax(0px, 1fr));
  grid-template-rows: repeat(5, minmax(0px, 1fr));
`;

const FeatureBlock = ({
  children,
  gridArea,
}: {
  children: React.ReactNode;
  gridArea: string;
}) => (
  <Box
    gridArea={gridArea}
    display="flex"
    justifyContent="center"
    flexDirection="column"
    alignItems="center"
    backgroundColor="grey-light-50"
    borderRadius={2}
    borderPosition={['all']}
    borderColor="grey-light-100"
  >
    {children}
  </Box>
);

const FeatureImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const TwoColumnGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${Theme.getBaseUnit(4)};
`;

export const LicenseUpgradeModalBody = ({
  account,
  upgradeToLicense,
  canSelfUpgradeViewerLicense,
  didUpgradeSuccessfully,
}: {
  account: CurrentAccount;
  upgradeToLicense: LicenseType;
  canSelfUpgradeViewerLicense: boolean;
  didUpgradeSuccessfully?: boolean;
}) => {
  const { customLicenseUpgradeLink } = account;
  const { formatMessage } = useTranslationOverrides();

  let introMessage: MessageDescriptor;
  let nextStepMessage: MessageDescriptor;

  switch (upgradeToLicense) {
    case 'standard':
      introMessage = messages.viewerIntro;
      nextStepMessage = messages.takeNextStep;
      break;
    default:
      assertUnreachable(upgradeToLicense);
  }

  let calloutContent: React.ReactNode = null;

  if (didUpgradeSuccessfully) {
    // Success message - shown after successful upgrade
    calloutContent = (
      <Box mt={6} p={3} backgroundColor="green-light" borderRadius={2}>
        <Text color="green-dark">
          <FormattedMessage {...messages.upgradeToStandardSuccess} />
        </Text>
      </Box>
    );
  } else if (canSelfUpgradeViewerLicense) {
    // Self-service info - shown when user can upgrade themselves
    calloutContent = (
      <Box mt={6} p={3} backgroundColor="grey-light-50" borderRadius={2}>
        <Text>
          <FormattedMessage {...messages.selfServiceUpgradeInfo} />
        </Text>
      </Box>
    );
  } else if (!customLicenseUpgradeLink) {
    // Fallback text - shown when no custom link and cannot self-upgrade
    calloutContent = (
      <Box mt={6} p={3} backgroundColor="grey-light-50" borderRadius={2}>
        <Text>
          <FormattedMessage {...messages.fallbackText} />
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Introduction */}
      <Box mb={6} mt={2}>
        <Text.Description>
          <FormattedMessage {...introMessage} />
        </Text.Description>
      </Box>

      <Box mb={6} position="relative">
        {/* Top labels */}
        <TwoColumnGrid>
          <Text.SectionHeader textAlign="center">
            {formatMessage(messages.trackKpisAndInitiatives)}
          </Text.SectionHeader>
          <Text.SectionHeader textAlign="center">
            {formatMessage(messages.draftAndOwnGoals)}
          </Text.SectionHeader>
        </TwoColumnGrid>

        {/* Inner Grid with Content Areas */}
        <FeaturesGridLayout
          display="grid"
          height={Theme.getBaseUnit(80)}
          mt={2}
          mb={2}
          flexGap={Theme.getBaseUnit(6)}
        >
          <FeatureBlock gridArea="1 / 1 / span 2 / 1">
            <FeatureImage
              src="https://workpath-platform-static.s3.eu-central-1.amazonaws.com/product-marketing/kpis.png"
              alt="KPI Chart"
            />
          </FeatureBlock>
          <FeatureBlock gridArea="1 / 2 / span 3 / 2">
            <FeatureImage
              src="https://workpath-platform-static.s3.eu-central-1.amazonaws.com/product-marketing/goals.png"
              alt="Goals"
            />
          </FeatureBlock>
          <FeatureBlock gridArea="3 / 1 / span 3 / 1">
            <FeatureImage
              src="https://workpath-platform-static.s3.eu-central-1.amazonaws.com/product-marketing/notes.png"
              alt="Notes"
            />
          </FeatureBlock>
          <FeatureBlock gridArea="4 / 2 / span 2 / 2">
            <FeatureImage
              src="https://workpath-platform-static.s3.eu-central-1.amazonaws.com/product-marketing/chat.png"
              alt="Chat"
            />
          </FeatureBlock>
        </FeaturesGridLayout>

        {/* Bottom Labels */}
        <TwoColumnGrid>
          <Text.SectionHeader textAlign="center">
            {formatMessage(messages.documentAchievements)}
          </Text.SectionHeader>
          <Text.SectionHeader textAlign="center">
            {formatMessage(messages.chatWithAiAgent)}
          </Text.SectionHeader>
        </TwoColumnGrid>
      </Box>

      {/* Call to action - only shown before upgrade */}
      {!didUpgradeSuccessfully && (
        <Box mb={4}>
          <Text>
            <FormattedMessage {...nextStepMessage} />
          </Text>
        </Box>
      )}

      {/* Info callout box */}
      {calloutContent}
    </Box>
  );
};
